import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { LogOut, Menu, Target } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import NotificationBell from "./NotificationBell";
import { BACKEND_URL } from "../lib/api";

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const brandHref = user ? (user.role === "admin" ? "/admin" : "/dashboard") : "/";

  const navItems = [
    { to: "/practice", label: "UI / Browser Automation" },
    { to: "/practice/api", label: "API Automation" },
    { to: "/jobs", label: "Jobs" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
  ];

  return (
    <header className="glass-header sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to={brandHref} className="flex items-center gap-2 group" data-testid="brand-logo">
          <span className="h-8 w-8 rounded-md bg-[#002FA7] text-white grid place-items-center font-display font-black">
            <Target className="h-4 w-4" strokeWidth={2.5} />
          </span>
          <span className="font-display text-lg font-bold tracking-tight text-zinc-950">
            Qa<span className="text-[#002FA7]">Testology</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive ? "text-[#002FA7] bg-blue-50" : "text-zinc-700 hover:text-zinc-950 hover:bg-zinc-50"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {user && <NotificationBell />}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2" data-testid="user-menu-btn">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="h-7 w-7 rounded-full object-cover" />
                  ) : user.avatar_path ? (
                    <img
                      src={`${BACKEND_URL}/api/profile/avatar/${user.user_id}?v=${user.avatar_updated_at || ""}`}
                      alt=""
                      className="h-7 w-7 rounded-full object-cover"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  ) : (
                    <span className="h-7 w-7 rounded-full bg-zinc-900 text-white grid place-items-center text-xs font-bold">
                      {user.name?.[0]?.toUpperCase() || "U"}
                    </span>
                  )}
                  <span className="hidden sm:inline text-sm font-medium">{user.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="font-normal">
                  <div className="font-medium">{user.name}</div>
                  <div className="text-xs text-zinc-500">{user.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                  Dashboard
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                    Admin panel
                  </DropdownMenuItem>
                )}
                {user.role === "job_seeker" && (
                  <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
                    My Profile
                  </DropdownMenuItem>
                )}
                {user.role === "employer" && (
                  <>
                    <DropdownMenuItem onClick={() => navigate("/employer/candidates")} data-testid="menu-candidates">
                      Browse candidates
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/employer/post-job")} data-testid="menu-post-job">
                      Post a Job
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                  <LogOut className="h-4 w-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button variant="ghost" size="sm" onClick={() => navigate("/login")} data-testid="header-login-btn">
                Sign in
              </Button>
              <Button
                size="sm"
                onClick={() => navigate("/signup")}
                data-testid="header-signup-btn"
                className="bg-[#002FA7] hover:bg-[#00227A] text-white"
              >
                Get started
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
